import { Component, computed, Input, ViewChild, inject } from '@angular/core';
import { OffcanvasService } from '../../../../../_services/offcanvas-service/offcanvas.service';
import { SubmitComponent } from '../../../../../shared/components/forms/submit/submit.component';
import { BoFormsModule } from '@bo/ng-forms';
import { AppFormComponent } from '../../../../../shared/classes/app-form';
import { Observable, of } from 'rxjs';
import { BusinessApiService } from '../../../../../_services/business-service/business-api.service';
import { ToastService } from '../../../../../_services/toast-service/toast.service';
import { FieldComponent } from '../../../../../shared/components/forms/form-field/form-field.component';
import { FileUploadComponent } from '../../../../../shared/components/file-upload/file-upload.component';
import { UploadFile } from 'ngx-uploader';
import { UploadedFile, UploadSettings } from '@bo/ng-uploads';
import { BankingDetails, BankingDetailsRequest, BankingStatus } from '../../../../../_interfaces/business.interface';
import { Method } from '@bo/ng-api';
import { UploadComponent } from '../../../../../shared/components/upload/upload.component';
import { HttpParams } from '@angular/common/http';
import { ModalComponent } from '../../../../../shared/components/modals/modal/modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { OffcanvasCancelBtnComponent } from '../../../../../shared/components/offcanvas/offcanvas-cancel-btn/offcanvas-cancel-btn.component';
import { BusinessService } from '../../../../../_services/business-service/business.service';
import { LoaderComponent } from '../../../../../shared/components/loader/loader.component';
import { OffcanvasBaseComponent } from '../../../../../shared/components/offcanvas/offcanvas-base/offcanvas-base.component';

@Component({
  selector: 'app-edit-banking-details',
  imports: [
    SubmitComponent,
    BoFormsModule,
    FieldComponent,
    FileUploadComponent,
    UploadComponent,
    OffcanvasCancelBtnComponent,
    LoaderComponent,
    OffcanvasBaseComponent,
  ],
  templateUrl: './edit-banking-details.component.html',
})
export class EditBankingDetailsComponent extends AppFormComponent<BankingDetailsRequest> {
  readonly offcanvasService = inject(OffcanvasService);
  readonly businessApiService = inject(BusinessApiService);
  readonly businessService = inject(BusinessService);
  readonly toastService = inject(ToastService);
  readonly modalService = inject(NgbModal);

  @ViewChild('uploadRef') uploadComponent!: UploadComponent;
  @Input() bankingDetails$!: Observable<BankingDetails>;
  protected bankingDetails = computed(() => this.businessService.bankingDetails());
  protected returnUrl: string = '/settings';
  returnParams = new HttpParams().append('page', 'banking');
  isExisting: boolean = false;
  status?: BankingStatus;
  protected readonly bankingStatus = BankingStatus;

  file?: UploadFile;
  documentUrl!: string;
  documentDownloadUrl!: string;
  uploadSettings: UploadSettings = {
    destination: 'banking-details',
    fileOwner: {
      owner_id: '',
      owner_type: '',
    },
  };

  protected fields: string[] = ['bank', 'branch_code', 'account_number', 'account_holder', 'account_type'];

  documentUploaded($event: UploadedFile) {
    this.documentUrl = $event.url;
    this.file = $event.file;
  }

  onChangeDocument($event: boolean) {
    if ($event) {
      this.documentUrl = '';
      this.file = undefined;
      this.uploadComponent?.reset();
    }
  }

  override createForm(): void {
    const options$ = this.businessApiService.getOptions(
      this.businessApiService.URL_BIZ + '/' + this.businessApiService.URL_BANKING_DETAILS,
      Method.POST,
      true,
    );
    const data$ = this.getInitialData();
    if (data$) {
      this.subscriptions.add(
        this.buildFromOptions(options$, data$).subscribe({
          next: () => {
            data$.subscribe({
              next: (res) => {
                if (res) {
                  this.isExisting = !!res.bank;
                  this.documentUrl = res.proof;
                  this.documentDownloadUrl = res.proof_download_url;
                  this.status = res.status;
                } else {
                  this.isExisting = false;
                  this.documentUrl = '';
                  this.documentDownloadUrl = '';
                }
              },
            });
          },
        }),
      );
    }
  }

  getInitialData() {
    if (this.bankingDetails()) {
      return of(this.bankingDetails() as BankingDetails);
    } else {
      return this.businessApiService.getBankingDetails();
    }
  }

  handleSubmitSuccess(): void {
    this.offcanvasService.close(this.returnUrl, this.returnParams);
    this.toastService.show(
      `Your banking details have been updated and are pending verification.`,
      'iconoir-check-circle',
    );
  }

  override onSubmit() {
    if (this.isExisting) {
      const modalRef = this.modalService.open(ModalComponent, {
        centered: true,
        size: 'md',
      });
      modalRef.componentInstance.title = 'Are you sure you want to update your banking details?';
      modalRef.componentInstance.bodyText =
        'By updating your banking details, the directors approval will be required for this action to be marked as complete.';
      modalRef.componentInstance.buttonTextAction = 'Update';
      modalRef.componentInstance.buttonTextDismiss = 'Cancel';
      modalRef.closed.subscribe({
        next: () => {
          super.onSubmit();
        },
      });
    } else {
      super.onSubmit();
    }
  }

  submitData(formData: BankingDetailsRequest): Observable<BankingDetailsRequest> {
    const data: BankingDetailsRequest = {
      ...formData,
      proof: this.documentUrl,
    };
    return this.businessApiService.createBankingDetails(data);
  }

  isValid(): boolean {
    return this.form.valid && !!this.documentUrl;
  }
}
